import { useRef, type CSSProperties } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { getResultMessage } from '../utils/survey';

interface ResultViewProps {
  score: number;
  total: number;
  countdown: number;
}

export function ResultView({ score, total, countdown }: ResultViewProps) {
  const rootRef = useRef<HTMLElement>(null);
  const scoreRingRef = useRef<HTMLDivElement>(null);
  const percent = Math.round((score / total) * 100);
  const message = getResultMessage(percent);

  useGSAP(
    () => {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reducedMotion ? 0.15 : 0.65;

      gsap.from('.result-copy > *', {
        opacity: 0,
        y: reducedMotion ? 0 : 24,
        duration,
        stagger: reducedMotion ? 0 : 0.07,
        ease: 'power3.out',
      });

      gsap.from('.score-wrap', {
        opacity: 0,
        scale: reducedMotion ? 1 : 0.82,
        duration: reducedMotion ? 0.15 : 0.75,
        ease: 'back.out(1.35)',
      });

      gsap.fromTo(
        scoreRingRef.current,
        { '--score': 0 },
        { '--score': percent, duration: reducedMotion ? 0.2 : 1.1, ease: 'power3.out' },
      );

      if (!reducedMotion) {
        gsap.to('.score-blob', {
          borderRadius: '58% 42% 39% 61% / 52% 56% 44% 48%',
          rotation: 3,
          duration: 5.5,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        });
      }
    },
    { scope: rootRef },
  );

  return (
    <section ref={rootRef} className="view result-view">
      <div className="result-copy">
        <span className="eyebrow">FIN DU QUIZ · شكراً لمشاركتك</span>
        <h1 className="result-title">MERCI POUR VOTRE PARTICIPATION !</h1>
        <p className="result-message">{message.fr}</p>
        <p className="result-message-ar" dir="rtl">{message.ar}</p>
        <div className="takeaways">
          <div className="takeaway">Lisez toujours l’étiquette avant utilisation.</div>
          <div className="takeaway">Ne mélangez jamais les produits sans instruction.</div>
          <div className="takeaway">Dosez, aérez et rangez hors de portée.</div>
        </div>
        <p className="restart-note">
          Le quiz redémarrera dans <strong>{countdown}</strong> secondes · Appuyez sur 1 pour recommencer.
        </p>
      </div>

      <div className="score-wrap">
        <div className="score-blob" />
        <div
          ref={scoreRingRef}
          className="score-ring"
          style={{ '--score': percent } as CSSProperties}
        >
          <div className="score-ring__content">
            <span className="score-ring__percent">{percent}%</span>
            <span className="score-ring__fraction">{score} / {total}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
